import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import type { Currency, Language } from 'codegen/types'
import { languageMap } from 'cf-i18n/constants'
import { useStoreSlug } from '@repo/cf-user/contexts/auth'
import { useIsLoggedIn } from './use-auth'

import { UserAvatar } from '../components/avatar'
import type { NavItem } from './use-nav-items'
import { useNavItems } from './use-nav-items'
import {
  useHeaderContext,
  useOnLogoutClick,
  useTranslations,
} from '../contexts/header-config-context'
import { HeartRoundedIcon } from '../components/icons/heart-rounded-icon'
import { User01Icon } from '../components/icons/user-01-icon'
import { Globe01Icon } from '../components/icons/globe-01-icon'
import { Speedometer03Icon } from '../components/icons/speedometer-03-icon'
import { FingerPrint01Icon } from '../components/icons/finger-print-01-icon'
import { CreditCardEditIcon } from '../components/icons/credit-card-edit-icon'
import { Flag05Icon } from '../components/icons/flag-05-icon'
import { GraduationHat01Icon } from '../components/icons/graduation-hat-01-icon'
import { StorefrontIcon } from '../components/icons/storefront-icon'
import { Edit05Icon } from '../components/icons/edit-05-icon'
import { Star01Icon } from '../components/icons/star-01-icon'
import { CodeSquare02Icon } from '../components/icons/code-square-02-icon'
import { AtSignIcon } from '../components/icons/at-sign-icon'
import { ReceiptIcon } from '../components/icons/receipt-icon'
import { NotificationTextIcon } from '../components/icons/notification-text-icon'
import { CreditCard02Icon } from '../components/icons/credit-card-02-icon'
import { LogOut01Icon } from '../components/icons/log-out-01-icon'
import { CodeBrowserIcon } from '../components/icons/code-browser-icon'
import { Link05Icon } from '../components/icons/link-05-icon'
import { Brush02Icon } from '../components/icons/brush-02-icon'
import { Settings01Icon } from '../components/icons/settings-01-icon'
import { LineChartUp04Icon } from '../components/icons/line-chart-up-04-icon'
import { Lightning01Icon } from '../components/icons/lightning-01-icon'
import { PresentationChartIcon } from '../components/icons/presentation-chart-icon'
import { BarLineChartIcon } from '../components/icons/bar-line-chart-icon'
import { SparkInpaintingIcon } from '../components/icons/spark-inpainting-icon'
import { EyeIcon } from '../components/icons/eye-icon'
import { ShippingIcon } from '../components/icons/shipping-icon'
import { useSecondaryNavItems } from './use-secondary-nav-items'
import { studioBaseUrl } from '../utils/urls'
import { Coin } from '@repo/studio-design-system-ui-coin'

import { useUserCoinBalanceQuery } from 'codegen/generated/coins'
import { useUsersOnlineValue } from '@repo/studio-online-presence/hooks/users-online-store'
import { IconFeaturedSeasonalAndGifts } from '@repo/design-system-cf/components/icons'

import * as React from 'react'

export type TopMenuItemType =
  | 'root'
  | 'account'
  | 'my-store'
  | 'language-switcher'
  | 'active-language'
  | 'active-currency'

export type NavItemType =
  | 'fonts'
  | 'fonts_sub'
  | 'images'
  | 'mockups'
  | 'kawaii'
  | 'cartoons'
  | 'backgrounds'
  | 'patterns'
  | 'frames'
  | 'quotes'
  | 'borders_and_corners'
  | 'sketches'
  | 'coloring_pages'
  | 'text_tools'
  | 'holidays_events'
  | 'popular_styles'
  | 'png'
  | 'graphics'
  | '3d'
  | '3d_crafts_paper_cut'
  | '3d_crafts_laser_cut'
  | 'spark'
  | 'crafts'
  | 'needlework'
  | 'photos'
  | 'embroidery'
  | 'knitting_patterns'
  | 'browse_all_needle_arts'
  | 'more_patterns'
  | 'animals'
  | 'around_the_world'
  | 'awareness_inspiration'
  | 'babies_kids'
  | 'family_friends'
  | 'fashion_beauty'
  | 'floral_garden'
  | 'food_drink'
  | 'holidays_celebrations'
  | 'house_home'
  | 'inspirational_quotes'
  | 'nature'
  | 'wedding'
  | 'work_religion_school'
  | 'more_categories'
  | 'classes'
  | 'learn'
  | 'blog'
  | 'video-classes'
  | 'craft-ideas'
  | 'tools'
  | 'pod'
  | 'bundles'
  | 'studio'
  | 'create'
  | 'templates'
  | 'ai_tools'
  | 'other_tools'
  | 'features'
  | 'start_designing'
  | 'subscription'
  | 'upgrade'
  | 'freebies'
  | 'gifts'
  | 'ask-cf'
  | 'cricut-machines'
  | 'cricut-cutting-machines'
  | 'cricut-heat-presses'
  | 'cricut-home'
  | 'cricut-features'
  | 'cricut-assets'
  | 'cricut-online-classes'
  | 'cricut-inspiration'
  | 'clubhouses'
  | 'clubhouse-cricut'
  | 'clubhouse-paper-crafts'
  | 'clubhouse-rhinestones'
  | 'clubhouse-siser'
  | 'clubhouse-xtool'
  | 'clubhouse-christmas'
  | '3d-crafts-laser-cut-arts-design'
  | '3d-crafts-laser-cut-celebrations'
  | '3d-crafts-laser-home-decor'
  | '3d-crafts-laser-jewelry'
  | '3d-crafts-laser-seasonal'
  | '3d-crafts-laser-toys'
  | '3d-printing'
  | 'downloadable-assets'

export type MenuItemType = TopMenuItemType | NavItemType

export interface MenuItem {
  id: string
  icon?: JSX.Element
  label: string
  link?: string
  target?: string
  onClick?: (e?: React.MouseEvent<HTMLAnchorElement>) => void
  track?: () => void
  subMenuId?: MenuItemType
}

export interface MenuGroup {
  groupName: string
  link?: string
  items: MenuItem[]
}

interface Menu {
  [id: string]: MenuGroup[]
}

const iconSize = 'h-5 w-5'
type MenuLinkDefinition = readonly [
  string,
  React.ComponentType<{ className?: string }>,
  Parameters<MenuTranslations>[0],
  string,
]
type MenuTranslations = ReturnType<typeof useTranslations>

const accountLinks: MenuLinkDefinition[] = [
  ['dashboard', Speedometer03Icon, 'account.my_dashboard', '/my-account/'],
  ['email-password', FingerPrint01Icon, 'account.edit_email_password', '/my-account/edit-account/'],
  ['billing', CreditCardEditIcon, 'account.billing_details', '/my-account/billing-details/'],
  ['shipping-address', ShippingIcon, 'account.shipping_address', '/my-account/shipping-address/'],
  ['favorites', HeartRoundedIcon, 'top_nav.favorites', '/my-favorites/'],
  ['following', Flag05Icon, 'account.followed_designers', '/following/'],
  ['my-classes', GraduationHat01Icon, 'account.my_classes', '/my-account/my-classes/'],
]
const accountSettingsLinks: MenuLinkDefinition[] = [
  ['edit-profile', Edit05Icon, 'account.edit_profile', '/my-account/my-profile/'],
  ['social-login', Star01Icon, 'account.social_accounts', '/my-account/social-accounts/'],
  [
    'download-licenses',
    CodeSquare02Icon,
    'account.download_licenses',
    '/my-account/license-keys-overview/',
  ],
  ['subscriptions', AtSignIcon, 'account.subscriptions', '/my-account/subscriptions/'],
  ['order-statement', ReceiptIcon, 'account.order_statements', '/my-account/orders/'],
  [
    'notification-settings',
    NotificationTextIcon,
    'account.notification_settings',
    '/my-account/email-settings/',
  ],
  ['payment-methods', CreditCard02Icon, 'account.payment_methods', '/my-account/payment-methods/'],
]
const affiliateLinks: MenuLinkDefinition[] = [
  [
    'affiliate-dashboard',
    Speedometer03Icon,
    'account.affiliate_dashboard',
    '/affiliate-area/?tab=urls',
  ],
  [
    'referral-url-gen',
    CodeBrowserIcon,
    'account.referal_url_gen',
    '/affiliate-area/referral-url-generator/',
  ],
  ['statistics', BarLineChartIcon, 'account.statistics', '/affiliate-area/?tab=stats'],
  ['referrals', Link05Icon, 'account.referrals', '/affiliate-area/?tab=referrals'],
  ['visits', EyeIcon, 'account.visits', '/affiliate-area/?tab=visits'],
  [
    'banners-and-creatives',
    Brush02Icon,
    'account.banners_creatives',
    '/affiliate-area/?tab=creatives',
  ],
  ['affiliate-settings', Settings01Icon, 'account.settings', '/affiliate-area/?tab=settings'],
]
const storeLinks: MenuLinkDefinition[] = [
  ['store-dashboard', LineChartUp04Icon, 'store.store_dashboard', '/dashboard/'],
  ['store-boost', Lightning01Icon, 'store.boost_store', '/dashboard/manage/boost/'],
  [
    'search-analytics',
    PresentationChartIcon,
    'store.search_analytics',
    '/dashboard/manage/search-analytics/',
  ],
  ['store-settings', Settings01Icon, 'store.store_settings', '/dashboard/personal-data/'],
]

function translateMenuLinks(definitions: MenuLinkDefinition[], t: MenuTranslations): MenuItem[] {
  return definitions.map(([id, Icon, label, link]) => ({
    id,
    icon: <Icon className={iconSize} />,
    label: t(label),
    link,
  }))
}

function OnlineIndicator() {
  return (
    <span className="relative mx-auto inline-flex size-2.5 rounded-full bg-clover-500 after:absolute after:inset-0 after:animate-ping after:rounded-full after:bg-clover-500" />
  )
}

function useHomepageMenuItems(isLoggedIn: boolean): MenuItem[] {
  const { isHomepage } = useHeaderContext()
  const t = useTranslations()
  const { data: coinBalance } = useUserCoinBalanceQuery(undefined, {
    select: d => d.userBalance.balance,
    enabled: isLoggedIn && isHomepage,
  })
  const onlineCount = useUsersOnlineValue()
  const coinItem: MenuItem = {
    id: 'coin-balance',
    icon: <Coin className={iconSize} />,
    label: Math.round(coinBalance ?? 0).toLocaleString(),
    link: AppRoutes.COINS,
  }
  return [
    {
      id: 'online-count',
      icon: <OnlineIndicator />,
      label: `${(onlineCount ?? 0).toLocaleString()} ${t('top_nav.online')}`,
      link: `${studioBaseUrl}/`,
      target: '_blank',
    },
    ...(isLoggedIn ? [coinItem] : []),
    {
      id: 'earn-coins',
      icon: <IconFeaturedSeasonalAndGifts className={iconSize} />,
      label: t('top_nav.earn_free_coins'),
      link: `${studioBaseUrl}/referrals`,
      target: '_blank',
    },
  ]
}

function useRootMenuItems(): MenuItem[] {
  const { lang, activeCurrency, isHomepage } = useHeaderContext()
  const isLoggedIn = useIsLoggedIn()
  const t = useTranslations()
  const homepageItems = useHomepageMenuItems(isLoggedIn)
  const accountItem: MenuItem = isLoggedIn
    ? { id: 'account', icon: <UserAvatar />, label: t('account.my_account'), subMenuId: 'account' }
    : {
        id: 'login',
        icon: <User01Icon className={iconSize} />,
        label: t('top_nav.login'),
        link: '/login/',
      }
  const favorites: MenuItem[] = isLoggedIn
    ? [
        {
          id: 'favorites',
          icon: <HeartRoundedIcon className={iconSize} />,
          label: t('top_nav.favorites'),
          link: '/my-favorites/',
        },
      ]
    : []
  return [
    accountItem,
    ...(isHomepage ? homepageItems : favorites),
    {
      id: 'create',
      icon: <SparkInpaintingIcon className={iconSize} />,
      label: t('top_nav.create'),
      link: '/studio/',
    },
    {
      id: 'activeLanguage',
      icon: <Globe01Icon className={iconSize} />,
      label: getLanguageCurrencyLabel(lang, activeCurrency),
      subMenuId: 'language-switcher',
    },
  ]
}

function useAccountMenuGroups(): MenuGroup[] {
  const t = useTranslations()
  const storeSlug = useStoreSlug()
  const onLogoutClick = useOnLogoutClick()
  const storeItem: MenuItem = storeSlug
    ? {
        id: 'my-store',
        icon: <StorefrontIcon className={iconSize} />,
        label: t('top_nav.store'),
        subMenuId: 'my-store',
      }
    : {
        id: 'open-store',
        icon: <StorefrontIcon className={iconSize} />,
        label: t('account.open_store'),
        link: '/open-store/',
      }
  return [
    {
      groupName: t('account.my_account'),
      items: [
        ...translateMenuLinks(accountLinks, t),
        storeItem,
        ...translateMenuLinks(accountSettingsLinks, t),
      ],
    },
    {
      groupName: '',
      items: [
        {
          id: 'signout',
          icon: <LogOut01Icon className={iconSize} />,
          label: t('account.sign_out'),
          onClick: onLogoutClick,
          link: '/logout/',
        },
      ],
    },
    { groupName: t('account.affiliates'), items: translateMenuLinks(affiliateLinks, t) },
  ]
}

function useStoreMenuGroups(): MenuGroup[] {
  const t = useTranslations()
  const storeSlug = useStoreSlug()
  return [
    {
      groupName: t('top_nav.store'),
      items: [
        {
          id: 'my-store',
          icon: <StorefrontIcon className={iconSize} />,
          label: t('top_nav.store'),
          link: `/designer/${storeSlug}/`,
        },
        ...translateMenuLinks(storeLinks, t),
      ],
    },
  ]
}

function useLanguageMenuGroups(): MenuGroup[] {
  const t = useTranslations()
  const { lang, activeCurrency } = useHeaderContext()
  const currencyItems: MenuItem[] = activeCurrency?.code
    ? [
        {
          id: 'active-currency',
          icon: (
            <div className={tw(iconSize, 'flex items-center justify-center text-lg font-medium')}>
              {activeCurrency.symbol}
            </div>
          ),
          label: activeCurrency.code.toUpperCase(),
          subMenuId: 'active-currency',
        },
      ]
    : []
  return [
    {
      groupName: t('language_currency.language_currency'),
      items: [
        {
          id: 'active-language',
          icon: <Globe01Icon className={iconSize} />,
          label: lang.toUpperCase(),
          subMenuId: 'active-language',
        },
        ...currencyItems,
      ],
    },
  ]
}

export const useMenuItems = (activeMenu: MenuItemType, extraNavItems?: NavItem[]) => {
  const navItems = useNavItemsForMobile(extraNavItems)
  const rootItems = useRootMenuItems()
  const account = useAccountMenuGroups()
  const store = useStoreMenuGroups()
  const language = useLanguageMenuGroups()
  const menus: Menu = {
    root: [
      { groupName: '', items: rootItems },
      { groupName: '', items: [...navItems.root] },
    ],
    account,
    'my-store': store,
    'language-switcher': language,
    ...navItems.subMenus,
  }
  return menus[activeMenu] ?? []
}

export const useNavItemsForMobile = (extraNavItems?: NavItem[]) => {
  const navItems = useNavItems()
  const secondaryNavItems = useSecondaryNavItems()
  let allItems = [...navItems, ...secondaryNavItems]

  if (extraNavItems?.length) {
    const studioIndex = allItems.findIndex(item => item.id === 'studio')
    const insertAt = studioIndex === -1 ? allItems.length : studioIndex + 1
    allItems = [...allItems.slice(0, insertAt), ...extraNavItems, ...allItems.slice(insertAt)]
  }

  return allItems.reduce(
    (acc, item, index) => {
      acc.root.push({
        id: item.name + index,
        label: item.name,
        link: item.link,
        target: item.target,
        track: item.track,
        ...(item.subMenuItems ? { subMenuId: item.id } : {}),
      })

      function getSubMenuItems(item: NavItem, index: number) {
        if (item.subMenuItems?.length && item.id) {
          // Flatten single-child wrappers: if an item has exactly one submenu child
          // that itself has subMenuItems, skip the intermediate level and show the
          // inner items directly (e.g. Image → [Image Tools → [tools...]] becomes
          // Image → [tools...] on mobile)
          const singleChildWrapper =
            item.subMenuItems.length === 1 && item.subMenuItems[0].subMenuItems?.length
              ? item.subMenuItems[0]
              : null
          const effectiveSubItems = singleChildWrapper
            ? singleChildWrapper.subMenuItems!
            : item.subMenuItems

          const submenu: MenuGroup = {
            groupName: singleChildWrapper?.name ?? item.name,
            link: item.link,
            items: effectiveSubItems.map(subItem => {
              getSubMenuItems(subItem, index)

              return {
                id: subItem.name + index,
                subMenuId: subItem.subMenuItems?.length ? subItem.id : undefined,
                icon: subItem.icon as JSX.Element | undefined,
                label: subItem.name,
                link: subItem.link,
                target: subItem.target,
              }
            }),
          }
          const secondarySubmenu: MenuGroup | undefined = item.secondarySubMenuItems?.length
            ? {
                groupName: item.secondaryName,
                items: item.secondarySubMenuItems?.map(subItem => {
                  getSubMenuItems(subItem, index)

                  return {
                    id: subItem.name + index,
                    subMenuId: subItem.subMenuItems?.length ? subItem.id : undefined,
                    icon: subItem.icon as JSX.Element | undefined,
                    label: subItem.name,
                    link: subItem.link,
                    target: subItem.target,
                  }
                }),
              }
            : undefined
          acc.subMenus[item.id] = secondarySubmenu ? [submenu, secondarySubmenu] : [submenu]
        }
      }

      getSubMenuItems(item, index)

      return acc
    },
    {
      root: [] as MenuItem[],
      subMenus: {} as Menu,
    },
  )
}

const getLanguageCurrencyLabel = (lang: Language, activeCurrency?: Currency) => {
  return activeCurrency
    ? `${getLanguageLabel(lang)} | ${activeCurrency.code?.toUpperCase()}`
    : getLanguageLabel(lang)
}

const getLanguageLabel = (lang: Language) => {
  return languageMap.get(lang) || lang
}
